/**
 * Admin workspace list/snapshot APIs do not return budget/capacity enrichment.
 * Mapper uses sentinel "UNKNOWN"; UI must never show that string as a real status.
 */

export type WorkspaceHealthStatusCode =
  | "OK"
  | "WARNING"
  | "BLOCKED"
  | "UNKNOWN";

export const WORKSPACE_HEALTH_NOT_YET_TRACKED = "Not yet tracked";

export function isWorkspaceHealthTracked(
  status: string | null | undefined,
): boolean {
  return Boolean(status) && status !== "UNKNOWN";
}

/** Human label — UNKNOWN → honest gap copy; real codes pass through. */
export function formatWorkspaceHealthStatus(
  status: string | null | undefined,
): string {
  if (!status || status === "UNKNOWN") return WORKSPACE_HEALTH_NOT_YET_TRACKED;
  return status;
}

type WorkspaceHealthStatusTextProps = {
  status: string | null | undefined;
  /** Prefix e.g. "Budget" — omitted when unused. */
  label?: string;
  className?: string;
};

/**
 * Visually distinct from computed statuses (muted + italic when untracked).
 */
export function WorkspaceHealthStatusText({
  status,
  label,
  className = "",
}: WorkspaceHealthStatusTextProps) {
  const tracked = isWorkspaceHealthTracked(status);
  const text = formatWorkspaceHealthStatus(status);
  const display = label ? `${label} ${text}` : text;
  return (
    <span
      data-testid="workspace-health-status"
      data-health-tracked={tracked ? "true" : "false"}
      className={
        tracked
          ? `text-inherit ${className}`.trim()
          : `text-slate-400 italic ${className}`.trim()
      }
    >
      {display}
    </span>
  );
}
